import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  DeleteMenuItemWithConfirmationModal,
  DeleteMenuItemWithConfirmationModalProps,
} from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { useReportError } from '@/hooks/useReportError';
import { LifetimeExclusionEventKind } from '@/types/schema';

import { useDeleteExemptionMutation } from './graphql/DeleteExemptionMenuItem.generated';

interface DeleteExemptionMenuItemProps
  extends Partial<DeleteMenuItemWithConfirmationModalProps> {
  lifetimeExclusionEventId: string;
  /** When provided, the associated LoggedTransfer will also be deleted */
  loggedTransferId?: string;
  kind: LifetimeExclusionEventKind;
  onDelete: () => void;
  modalProps?: Record<string, unknown>;
  menuItemProps?: Record<string, unknown>;
}

export function DeleteExemptionMenuItem({
  lifetimeExclusionEventId,
  loggedTransferId,
  kind,
  onDelete,
  modalProps = {},
  menuItemProps = {},
  ...deleteProps
}: DeleteExemptionMenuItemProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const itemWord =
    kind === LifetimeExclusionEventKind.Gift ? 'gift' : 'exemption';

  const [deleteExemption, { loading: deleting }] = useDeleteExemptionMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onError: (error) => {
      reportError(`Failed to delete ${itemWord}`, error);
      showFeedback(
        `Failed to delete this ${itemWord}. Please refresh the page and try again.`
      );
    },
    onCompleted: () => {
      showFeedback(
        `${itemWord.charAt(0).toUpperCase() + itemWord.slice(1)} deleted successfully`,
        {
          variant: 'success',
        }
      );
      onDelete();
    },
  });

  const handleDelete = () => {
    return deleteExemption({
      variables: {
        deleteLifetimeExclusionEventId: lifetimeExclusionEventId,
        // When there's no loggedTransferId, pass a placeholder — the field is @include(if: $shouldDeleteTransfer) skipped
        deleteLoggedTransferId: loggedTransferId ?? '',
        shouldDeleteTransfer: !!loggedTransferId,
      },
    });
  };

  return (
    <DeleteMenuItemWithConfirmationModal
      label={`Delete ${itemWord}`}
      onConfirmDelete={handleDelete}
      {...deleteProps}
      menuItemProps={{ loading: deleting, ...menuItemProps }}
      modalProps={{
        heading: `Are you sure you want to delete this ${itemWord}?`,
        ...modalProps,
      }}
    />
  );
}
